import { Building, CogIcon, PersonIcon } from 'assets/icons';
import { generateEmployeeColor, dateWordMonthShortHour } from 'helper/helper';

/**
 * One line of an activity timeline.
 *
 * Used by the company panel and the time sheet history. The mission tab shows
 * the same rendering but still carries its own copy of it — this component was
 * extracted from that copy, which is the one the design was drawn against.
 *
 * Presentational only. Each timeline works out who acted and what to say; this
 * decides how it looks.
 */
const initialsOf = (person) =>
  `${(person?.firstname || '')[0] || ''}${(person?.lastname || '')[0] || ''}`.toUpperCase();

export const fullNameOf = (person) =>
  [person?.firstname, person?.lastname].filter(Boolean).join(' ').trim();

const ActivityEntry = ({
  person,
  displayName,
  date,
  dateLabel,
  /** 'company', 'person' or 'system' when there is nobody to show initials for. */
  fallbackIcon,
  children,
}) => {
  const initials = initialsOf(person);
  const name = displayName ?? fullNameOf(person);

  return (
    <div className="group">
      <div className="flex gap-3 px-3 py-4 hover:bg-gray-50 transition-colors rounded-lg">
        <div className="flex-shrink-0 pt-0.5">
          {fallbackIcon || !initials ? (
            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 border border-gray-200">
              {fallbackIcon === 'person' ? (
                <PersonIcon wh={16} color="#6B7280" />
              ) : fallbackIcon === 'system' ? (
                <CogIcon wh={16} color="#6B7280" />
              ) : (
                <Building wh={16} color="#6B7280" />
              )}
            </div>
          ) : (
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-medium text-white"
              style={{
                backgroundColor: generateEmployeeColor(
                  person?.firstname,
                  person?.lastname
                ),
              }}
            >
              {initials}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">{name}</span>
            <span className="text-xs text-gray-400">
              · {dateLabel ?? dateWordMonthShortHour(date)}
            </span>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

export default ActivityEntry;
